import React from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import DuoButton from '../components/DuoButton';
import ProductCard from '../components/ProductCard';
import TeamSection from '../components/TeamSection';
import Reveal from '../components/Reveal';
import { RussianGgLogo, SevilLogo } from '../components/ProductLogos';
import { CONTACT_EMAIL } from '../components/layout/SiteFooter';
import { PALETTE, RADIUS } from '../theme';
import { products } from '../data/products';
import { useT } from '../lib/i18n';

/**
 * Vaqtly Holding's single page.
 *
 * Duolingo's page rhythm: one idea per band, generous vertical space, a heavy
 * short headline over a normal-weight support line, and a coloured full-bleed
 * band carrying the closing action.
 *
 * Section ids stay in English in every locale — they are addresses that end up
 * in shared links, and a URL fragment that changes with the language breaks any
 * link sent before the reader switched.
 */
export default function LandingPage() {
  return (
    <Box id="top">
      <Hero />
      <Products />
      {/* Straight after the products: the groups are per-product, so they read as
          a continuation of the tiles rather than a separate roster. */}
      <TeamSection />
      <Principles />
      <Contact />
    </Box>
  );
}

/* ------------------------------------------------------------------------ hero */

function Hero() {
  const t = useT();

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 7, md: 12 }, pb: { xs: 8, md: 12 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={{ xs: 7, md: 6 }}>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', md: 'left' } }}>
          <Reveal>
            <Eyebrow>{t.hero.eyebrow}</Eyebrow>
          </Reveal>

          <Reveal delay={80}>
            <Typography
              variant="h1"
              sx={{ mt: 2, fontSize: { xs: 38, sm: 50, md: 60 }, fontWeight: 800, color: PALETTE.ink }}
            >
              {t.hero.headlinePre}
              <Box component="span" sx={{ color: PALETTE.signal }}>
                {t.hero.headlineAccent}
              </Box>
              {t.hero.headlinePost}
            </Typography>
          </Reveal>

          <Reveal delay={160}>
            <Typography
              sx={{
                mt: 3,
                maxWidth: 520,
                mx: { xs: 'auto', md: 0 },
                fontSize: { xs: 17, md: 19 },
                color: PALETTE.inkMuted,
              }}
            >
              {t.hero.body}
            </Typography>
          </Reveal>

          <Reveal delay={240}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mt: 5, justifyContent: { xs: 'stretch', md: 'flex-start' } }}
            >
              <DuoButton duoSize="lg" component="a" href="#products">
                {t.hero.ctaPrimary}
              </DuoButton>
              <DuoButton duoSize="lg" duoVariant="secondary" component="a" href="#contact">
                {t.hero.ctaSecondary}
              </DuoButton>
            </Stack>
          </Reveal>
        </Box>

        {/* The two marks, breathing on their own offsets so they never sync up. */}
        <Reveal delay={200} animation="duoPop" sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ position: 'relative', width: { xs: 260, md: 320 }, height: { xs: 240, md: 300 } }}>
            <FloatingMark sx={{ top: 0, left: { xs: 0, md: 10 } }} delay="0s">
              <RussianGgLogo size={148} />
            </FloatingMark>
            <FloatingMark sx={{ bottom: 0, right: 0 }} delay="1.4s">
              <SevilLogo size={168} />
            </FloatingMark>
          </Box>
        </Reveal>
      </Stack>
    </Container>
  );
}

function FloatingMark({ children, delay, sx }: { children: React.ReactNode; delay: string; sx?: object }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        borderRadius: '22%',
        /* The same solid edge the buttons sit on, so the marks read as objects
           resting on the page rather than images floating over it. */
        boxShadow: '0 6px 0 0 rgba(0,0,0,0.10)',
        animation: `duoFloat 5.5s ease-in-out ${delay} infinite`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/* -------------------------------------------------------------------- products */

function Products() {
  const t = useT();

  return (
    <Box id="products" sx={{ scrollMarginTop: 96, backgroundColor: PALETTE.groundSunken, py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Reveal>
          <Eyebrow>{t.products.eyebrow}</Eyebrow>
          <Typography
            variant="h2"
            sx={{ mt: 1.5, fontSize: { xs: 30, md: 42 }, fontWeight: 800, color: PALETTE.ink }}
          >
            {t.products.heading}
          </Typography>
        </Reveal>

        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          }}
        >
          {products.map((product, index) => (
            <Reveal key={product.id} delay={index * 120}>
              <Box sx={{ height: '100%' }}>
                <ProductCard product={product} />
              </Box>
            </Reveal>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

/* ------------------------------------------------------------------ principles */

/** One accent per card, in the order the cards appear. */
const PRINCIPLE_COLORS = [PALETTE.signal, PALETTE.green, PALETTE.gold];

function Principles() {
  const t = useT();

  return (
    <Box id="about" sx={{ scrollMarginTop: 96, py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Reveal>
          <Eyebrow>{t.principles.eyebrow}</Eyebrow>
          <Typography
            variant="h2"
            sx={{ mt: 1.5, fontSize: { xs: 30, md: 42 }, fontWeight: 800, color: PALETTE.ink, maxWidth: 720 }}
          >
            {t.principles.heading}
          </Typography>
        </Reveal>

        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          }}
        >
          {t.principles.items.map((principle, index) => (
            <Reveal key={principle.title} delay={index * 110}>
              <Box
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: `${RADIUS.card}px`,
                  border: `2px solid ${PALETTE.hairline}`,
                  boxShadow: `0 4px 0 0 ${PALETTE.hairline}`,
                }}
              >
                {/* A colour block instead of an icon: no icon set to license, and it
                    keeps the three cards distinguishable at a glance. */}
                <Box
                  sx={{
                    width: 44,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: PRINCIPLE_COLORS[index % PRINCIPLE_COLORS.length],
                    mb: 2.5,
                  }}
                />
                <Typography sx={{ fontSize: 19, fontWeight: 800, color: PALETTE.ink }}>{principle.title}</Typography>
                <Typography sx={{ mt: 1, fontSize: 16, color: PALETTE.inkMuted }}>{principle.body}</Typography>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

/* ---------------------------------------------------------------------- contact */

function Contact() {
  const t = useT();

  return (
    <Container id="contact" maxWidth="lg" sx={{ scrollMarginTop: 96 }}>
      <Reveal animation="duoPop">
        <Box
          sx={{
            px: { xs: 3, md: 8 },
            py: { xs: 6, md: 9 },
            borderRadius: `${RADIUS.card * 1.5}px`,
            backgroundColor: PALETTE.signal,
            boxShadow: `0 6px 0 0 ${PALETTE.signalDepth}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: '#ffffff' }}>
            {t.contact.heading}
          </Typography>
          <Typography sx={{ mt: 2, fontSize: { xs: 16, md: 18 }, color: 'rgba(255,255,255,0.92)' }}>
            {t.contact.body}
          </Typography>
          <Stack direction="row" justifyContent="center" sx={{ mt: 4 }}>
            {/* White face on the blue band: the one place the secondary variant is
                the loudest control on screen. */}
            <DuoButton duoSize="lg" duoVariant="secondary" component="a" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </DuoButton>
          </Stack>
        </Box>
      </Reveal>
    </Container>
  );
}

/* --------------------------------------------------------------------- helpers */

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="p"
      sx={{
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: PALETTE.signal,
      }}
    >
      {children}
    </Typography>
  );
}
